import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Search, Link2, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { planToTier, canSaveCard } from "@/lib/tierGate";
import { isOwner } from "@/lib/owner";
import { SafeImg } from "@/components/SafeImg";
import { toast } from "@/components/ui/sonner";
import { getVault, saveToVault, removeFromVault } from "@/lib/api";
import { SignInModal } from "@/components/SignInModal";
import PwaNudgeBanner from "@/components/PwaNudgeBanner";
import { isStandalone } from "@/lib/pwa";
import Dashboard from "@/pages/Dashboard";
import DiscoverView from "@/features/snaggedify/DiscoverView";
import CheckView from "@/features/snaggedify/CheckView";
import UpgradeWallModal from "@/features/snaggedify/UpgradeWallModal";
import PdpModal from "@/features/snaggedify/PdpModal";
import ProfileModal from "@/components/ProfileModal";
import SettingsTab from "@/components/SettingsTab";

// Snaggedify Core App Shell — guided, retailer-first navigation: HOME / DISCOVER /
// CHECK LINK. Home replaces the old public infinite Scroll feed (Section 1 of the
// Retailer-First pivot) — no more background polling, only intentional searches.
const TABS = [
  { id: "home", label: "Home", icon: Home },
  { id: "discover", label: "Discover", icon: Search },
  { id: "check", label: "Check Link", icon: Link2 },
];

export default function Workspace() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("home");
  const [showProfile, setShowProfile] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [upgradeWallTier, setUpgradeWallTier] = useState(null); // Section 5 — required tier to unlock, or null
  const [pdpDeal, setPdpDeal] = useState(null); // Section 8 — PDP modal overlay target
  const [vaultIds, setVaultIds] = useState(new Set()); // Save-to-Vault state, independent of any single tab's feed
  const [showSignIn, setShowSignIn] = useState(false); // Guest Mode — soft-auth prompt
  const [guestCheckedLink, setGuestCheckedLink] = useState(false);
  const [pwaNudgeDismissed, setPwaNudgeDismissed] = useState(() => {
    try { return localStorage.getItem("pwa_nudge_dismissed") === "1"; } catch { return false; }
  });

  const isGuest = !!user?.is_guest;
  const goToPricing = () => navigate("/pricing");
  const showPwaNudge = isGuest && !pwaNudgeDismissed && !isStandalone() && guestCheckedLink;
  const dismissPwaNudge = () => { setPwaNudgeDismissed(true); try { localStorage.setItem("pwa_nudge_dismissed", "1"); } catch { /* ignore */ } };

  // Section 5 — Freemium Tier Engine. Gating tier is derived straight from `user.plan`
  // (mirrors backend's tier_service.tier_for 1:1), so it updates automatically after
  // checkAuth() refetches the account post-upgrade (real Stripe or dev upgrade-sim).
  // Owner/Admin bypass — any admin account (or the hardcoded owner email) always sees
  // every paywalled surface as ELITE, regardless of their actual stored plan. Mirrors
  // the equivalent backend override in tier_service.tier_for() so Vault limits, Deep
  // Scan credits, and PDP reviews/tax/breakdown all unlock consistently server + client.
  const currentTier = isOwner(user) ? "ELITE" : planToTier(user?.plan);
  const openLockedUpgrade = (requiredTier) => setUpgradeWallTier(requiredTier || "PLUS");

  // Vault-save limit (FREE 5 / PLUS 50 / PRO+ELITE unlimited), self-contained (no
  // dependency on any tab's local feed state) so Save-to-Vault works identically
  // from the Dashboard Spotlight, Discover, Check Link, Guided Search, and History.
  const guardedToggleSave = async (deal) => {
    if (!deal) return;
    // Guest Mode — Save/Vault is a soft-auth action: prompt Sign In, never the tier
    // upgrade wall, so guests aren't told to "upgrade" an account they don't have.
    if (isGuest) { setShowSignIn(true); return; }
    if (vaultIds.has(deal.id)) {
      setVaultIds((prev) => { const next = new Set(prev); next.delete(deal.id); return next; });
      removeFromVault(deal.id).catch(() => {}); // best-effort — local UI already updated
      return;
    }
    const gate = canSaveCard(currentTier, vaultIds.size);
    if (gate.locked) { openLockedUpgrade(gate.requiredTier); return; }
    try {
      const res = await saveToVault({
        deal_id: deal.id, title: deal.title, price: deal.price, msrp: deal.msrp,
        verdict: deal.verdict, image: deal.image || "", reseller: deal.reseller || "", outbound_url: deal.outboundUrl || "",
        fulfillment_mode: deal.fulfillment || "ONLINE_SHIPPING",
      });
      if (!res.already_saved) setVaultIds((prev) => new Set(prev).add(deal.id));
    } catch (err) {
      if (err?.status === 402) { openLockedUpgrade(err?.data?.tier === "FREE" ? "PLUS" : "PRO"); return; }
      toast.error("Couldn't save — try again.");
    }
  };

  // Section 8 — hydrate the real Vault on load, so a returning user's saves persist
  // across every entry point (Dashboard Spotlight, Discover, Check, Guided Search).
  useEffect(() => {
    if (!user || user.is_guest) return;
    getVault().then((res) => setVaultIds(new Set((res.items || []).map((it) => it.deal_id)))).catch(() => {});
  }, [user?.user_id]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [tab]);

  // Landing's "Check a Link" secondary CTA sets this before navigating to /app, so
  // guests land directly on the Check tab instead of the default Home dashboard.
  useEffect(() => {
    try {
      const launchTab = sessionStorage.getItem("snaggedify_launch_tab");
      if (launchTab === "check" || launchTab === "discover") {
        setTab(launchTab);
        sessionStorage.removeItem("snaggedify_launch_tab");
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id") || params.get("checkout")) {
      toast.success("🎉 Subscription updated!");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Guest Mode — one-time friendly nudge on first arrival, letting guests know what
  // signing in unlocks without blocking anything they're already doing.
  useEffect(() => {
    if (!isGuest) return;
    try {
      if (localStorage.getItem("has_seen_guest_nudge")) return;
      localStorage.setItem("has_seen_guest_nudge", "1");
    } catch {
      return;
    }
    toast.message("Browsing as guest — Sign in anytime to save items to your Vault and turn on alerts!");
  }, [isGuest]);

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div id="snaggedify-app-root" data-testid="workspace" className="flex min-h-screen flex-col overflow-x-hidden bg-slate-50 text-slate-900 dark:bg-charcoal dark:text-white supports-[height:100dvh]:min-h-[100dvh]">
      {/* GLOBAL HEADER */}
      <header id="global-header" className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur-xl dark:border-hairline dark:bg-charcoal/90">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-2 px-4 md:px-6">
          <button onClick={() => (isGuest ? navigate("/") : setTab("home"))} data-testid="brand-title" className="brand-title flex min-w-0 shrink-0 items-center gap-2" aria-label={isGuest ? "Go to Home" : "Go to Dashboard"}>
            <SafeImg src="/slip-logo.png" alt="Snaggedify" className="h-8 w-8 shrink-0 rounded-lg" />
            <span className="hidden whitespace-nowrap text-sm font-black tracking-tighter min-[400px]:inline">
              Snaggedify <span className="font-bold text-slate-400 dark:text-zinc-500">by Cheapify</span>
            </span>
            <span data-testid="alpha-badge" className="shrink-0 whitespace-nowrap rounded-full border border-brand-orange/40 bg-brand-orange/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-brand-orange">{isOwner(user) ? "Alpha: Owner" : "Alpha"}</span>
          </button>

          {/* Desktop top navigation (SECONDARY viewport rule: header nav on desktop) */}
          <nav data-testid="desktop-nav" className="hidden items-center gap-1 md:flex">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-tab={t.id.toUpperCase()}
                  data-testid={`desktop-nav-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-black transition-colors ${
                    active ? "bg-gradient-to-r from-brand-yellow to-brand-orange text-slate-950" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={2.5} /> {t.label}
                </button>
              );
            })}
            <button
              onClick={goToPricing}
              data-testid="desktop-nav-upgrade"
              className="ml-1 flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-4 py-2 text-sm font-black text-slate-950 transition-transform hover:scale-[1.03]"
            >
              <Crown className="h-4 w-4" strokeWidth={2.5} /> Upgrade
            </button>
          </nav>

          <div className="flex min-w-0 items-center justify-end gap-1.5 min-[400px]:gap-2 md:gap-2.5">
            <button
              onClick={goToPricing}
              data-tier={currentTier}
              data-testid="tier-badge"
              className="user-tier-badge hidden h-9 shrink-0 items-center whitespace-nowrap rounded-full border border-brand-yellow/40 bg-brand-yellow/10 px-2.5 text-xs font-black uppercase tracking-[0.04em] text-brand-orange md:inline-flex"
            >
              {isOwner(user) ? "OWNER" : `Tier: ${currentTier}`}
            </button>
            <div className="credit-counter hidden items-center gap-1 whitespace-nowrap text-xs font-bold text-slate-500 dark:text-zinc-400 md:flex" data-testid="credit-counter">
              Credits: <span id="credit-count" data-testid="credit-count">{isOwner(user) ? "∞" : (user?.credits ?? 0)}</span>
            </div>
            {isGuest ? (
              <>
                <button
                  onClick={() => navigate("/")}
                  id="header-home-link"
                  data-testid="header-home-link"
                  className="inline-flex h-9 shrink-0 items-center whitespace-nowrap px-1.5 text-xs font-bold text-slate-500 transition-colors hover:text-brand-orange dark:text-zinc-400"
                >
                  About
                </button>
                <button
                  onClick={() => setShowSignIn(true)}
                  id="header-signin-btn"
                  data-testid="header-signin-btn"
                  className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-4 text-xs font-black uppercase tracking-[0.06em] text-slate-950 transition-transform hover:scale-[1.04]"
                >
                  Sign In
                </button>
              </>
            ) : (
              <button onClick={() => setShowProfile(true)} id="open-profile-btn" data-testid="profile-avatar-btn" title="Profile" aria-label="Profile" className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-brand-yellow to-brand-orange">
                {user?.picture && !avatarError ? (
                  <img src={user.picture} alt={user.name} onError={() => setAvatarError(true)} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs font-black text-slate-950">{initial}</span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* MAIN 3-TAB VIEWPORT ROUTER */}
      <main id="main-viewport" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 pb-28 md:px-6 md:pb-10 sm:py-8">
        {tab === "settings" ? (
          <SettingsTab />
        ) : (
          <>
            <Dashboard active={tab === "home"} onOpenPdp={setPdpDeal} />
            <DiscoverView active={tab === "discover"} currentTier={currentTier} onLocked={openLockedUpgrade} onOpenPdp={setPdpDeal} location={user?.location} />
            <CheckView active={tab === "check"} onOpenUpgrade={goToPricing} onOpenPdp={setPdpDeal} onResult={() => setGuestCheckedLink(true)} />
          </>
        )}
      </main>

      {/* PRIMARY NAVIGATION — fixed bottom bar on mobile/PWA. Exactly 4 tabs: Home |
          Discover | Check Link | Upgrade. Profile is deliberately NOT here — the
          header avatar (top-right) is the sole Profile entry point on every viewport,
          avoiding a duplicate touchpoint. Icon strokeWidth stays constant across
          active/inactive states (only color + a fixed-size background pill change) so
          toggling tabs never shifts layout. */}
      <nav id="primary-navigation" data-testid="mobile-bottom-nav" className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl dark:border-hairline dark:bg-charcoal/90 md:hidden">
        <div className="mx-auto flex max-w-4xl items-stretch justify-around px-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                className={`nav-btn flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1 px-1 py-2.5 text-center text-[11px] font-bold uppercase leading-none tracking-wide transition-colors ${active ? "active text-brand-orange" : "text-slate-400 dark:text-zinc-500"}`}
                data-tab={t.id.toUpperCase()}
                data-testid={`mobile-nav-${t.id}`}
                onClick={() => setTab(t.id)}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${active ? "bg-brand-orange/10" : "bg-transparent"}`}>
                  <Icon className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <span className="w-full truncate leading-none">{t.label}</span>
              </button>
            );
          })}
          <button onClick={goToPricing} className="nav-btn flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1 px-1 py-2.5 text-center text-[11px] font-bold uppercase leading-none tracking-wide text-slate-400 transition-colors dark:text-zinc-500" data-testid="mobile-nav-upgrade">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-transparent">
              <Crown className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="w-full truncate leading-none">Upgrade</span>
          </button>
        </div>
      </nav>

      {/* Section 5 — Upgrade Wall, opened whenever a locked save/tier surface is hit */}
      <UpgradeWallModal
        open={!!upgradeWallTier}
        onClose={() => setUpgradeWallTier(null)}
        currentTier={currentTier}
        requiredTier={upgradeWallTier}
        onOpenRealUpgrade={() => { setUpgradeWallTier(null); goToPricing(); }}
        onTierChanged={() => { setUpgradeWallTier(null); checkAuth(); }}
      />

      {/* Section 8 — PDP overlay, opened from any card's "Review Product" tap across
          Home/Discover/Check Link/History. "Snag Deal" inside is the only outbound
          trigger; the heart button is the ONLY Save-to-Vault entry point app-wide. */}
      <PdpModal deal={pdpDeal} open={!!pdpDeal} onClose={() => setPdpDeal(null)} currentTier={currentTier} location={user?.location} onLocked={openLockedUpgrade} onOpenPdp={setPdpDeal} isSaved={pdpDeal ? vaultIds.has(pdpDeal.id) : false} onToggleSave={() => guardedToggleSave(pdpDeal)} />

      <ProfileModal
        open={showProfile}
        onClose={() => setShowProfile(false)}
        onOpenUpgrade={goToPricing}
        onOpenSettings={() => setTab("settings")}
        onOpenDash={() => setTab("home")}
        onOpenInspect={() => setTab("check")}
        onOpenCreateCard={() => setTab("check")}
        onOpenPdp={setPdpDeal}
      />

      {/* Guest Mode — soft-auth sign-in prompt for Save/Vault & Notifications */}
      <SignInModal
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        title="Sign in to unlock this"
        subtitle="Create a free account to save deals and turn on alerts."
      />

      <PwaNudgeBanner show={showPwaNudge} onDismiss={dismissPwaNudge} />
    </div>
  );
}
